import { get } from 'lodash';
import moment from 'moment';
import {
  PathologyAlertResult,
  PathologyResultAlertPath,
  PathologyValuePath,
  TimelineItemType,
} from './type';
import type { MedicationEventDetail } from '../../../lib/transferObjects/Event';

export interface EventTimelineItem {
  id?: string | number;
  event_date?: string;
  prescription_written?: string;
  effective_time?: string;
  pd_dispense_record_link?: string;
  pd_prescription_record_link?: string;
  pd_dispense_id?: string;
  pbs_id?: string;
  pd_summary_id?: string;
  pd_prescription_id?: string;
  survey_type?: string;
  settings?: {
    dosageChange?: boolean;
  };
  detail?: MedicationEventDetail;
  event_name?: string;
  [key: string]: any; // For additional properties that might be spread from the event object
}

export interface TimelineItem {
  id: string | number;
  group: string | number;
  start_time: number;
  end_time: number;
  type: string;
  event_id?: string | number;
  order: number;
  group_type: string;
  parent_name: string;
  group_parent_name: string;
  cnt: number;
  showInSimple: boolean;
  showInComplex: boolean;
  showInExpanded: boolean;
  showInCollapsed: boolean;
  med_type?: string;
  [key: string]: any; // For additional properties from the event object
}

const tempItem = {
  cnt: 0,
  showInSimple: true,
  showInComplex: false,
  showInExpanded: true,
  showInCollapsed: true,
};

export const EventTimeline = (
  event: EventTimelineItem,
  type: string,
  group: string | number,
  groupType: string,
  key: string | number,
  order: number,
  parentName: string,
  groupParentName: string,
  isAutomedic: boolean,
): TimelineItem => {
  let Item: TimelineItem = {
    ...tempItem,
    ...event,
    type: type,
    event_id: event.id,
    id: key,
    group: group,
    start_time: moment(event.event_date).valueOf(),
    end_time: moment(event.event_date).add(1, 'hour').valueOf(),
    order: order,
    group_type: groupType,
    parent_name: parentName,
    group_parent_name: groupParentName,
  };

  if (isAutomedic) {
    if (event.detail?.fhir?.resourceType === 'MedicationRequest') {
      Item.type = 'emrOrder';
    } else if (
      event.detail?.homeMedication ||
      event.detail?.fhir?.resourceType === 'MedicationDispense'
    ) {
      Item.type = 'homeMedication';
    }

    if (
      event.detail?.scriptWritten &&
      typeof event.detail.scriptWritten === 'string' &&
      event.detail?.scriptWritten?.toLowerCase() === 'true'
    ) {
      Item.type = 'scriptWritten';
    }

    if (
      (event.pd_dispense_record_link && event.pd_summary_id) ||
      event.pbs_group_id
    ) {
      Item.type = 'dispense';
    }

    if (event.pd_prescription_record_link) {
      Item.start_time = moment(event.prescription_written).valueOf();
      Item.end_time = moment(event.prescription_written)
        .add(1, 'hour')
        .valueOf();
      Item.med_type = 'Prescription';
      Item.type = 'automedicPrescription';
    }
  } else {
    if (event.pd_prescription_record_link) {
      Item.start_time = moment(event.prescription_written).valueOf();
      Item.end_time = moment(event.prescription_written)
        .add(1, 'hour')
        .valueOf();
      Item.med_type = 'Prescription';
      Item.type = 'prescription';
    }
  }

  if (event.pd_dispense_record_link) {
    Item.start_time = moment(event.effective_time).valueOf();
    Item.end_time = moment(event.effective_time).add(1, 'hour').valueOf();
    Item.med_type = 'Medication';
  }

  if (groupType.startsWith('Appointment') && type === 'dotBlue') {
    Item.type = 'dotDarkBlue';
  }

  if (
    groupType.startsWith('Diagnostic and Pathology') ||
    (groupType.startsWith('Other Services') && type === 'dotBlue')
  ) {
    Item.type = 'dotPurple';
  }

  if (event.detail?.icon) {
    Item.type = event.detail.icon;
  }

  // Medicine profile events should show the doctor icon (same as prescription)
  if (
    event.event_category === 'Medicine Profile' ||
    (typeof event.id === 'string' && event.id.startsWith('med-profile-'))
  ) {
    Item.type = 'dotMedProfile';
  }

  // test for pathology
  const isPathologyResultNotNormal = get(Item, PathologyResultAlertPath, null);
  const pathologyValue = get(Item, PathologyValuePath, null);

  if (pathologyValue) {
    Item.type = TimelineItemType.DotGray;
    if (isPathologyResultNotNormal && isPathologyResultNotNormal.code) {
      if (
        PathologyAlertResult.UndetectableOruUnknown !==
        isPathologyResultNotNormal.code
      ) {
        Item.type = TimelineItemType.DotRed;
      }
    }
  }

  let name = Item.parent_name;
  Item.parent_name = name.charAt(0).toUpperCase() + name.slice(1);

  name = Item.group_type;
  Item.group_type = name.charAt(0).toUpperCase() + name.slice(1);

  return Item;
};
